import React from 'react';
import history from '../history';

/**
 * MobileBottomNav — shared bottom nav bar for mobile views (redesign #86bb5dx62).
 * Consolidated to Live / Playback / Setup for all product types.
 * Used by LiveView, PlaybackView, and MCPlayback.
 *
 * Props:
 *   activePage: 'live' | 'playback' | 'mcplayback' | 'setup'
 *   fPlayback: number (1 = playback enabled)
 *   mobileMenuOpen: string | null
 *   onToggleMenu: function
 *   onSetup: function — optional override for the Setup action. Defaults to routing to the shared
 *            /setup SetupView shell (David 2026-08-05) — the old standalone overlay is retired.
 *   onLogout: function — legacy Logout button for views that still pass it (Live
 *            now surfaces logout inside Setup instead, so it omits this prop).
 *   guardNav: function(proceed) — optional. Wraps every navigation so the parent can
 *            intercept (e.g. Live prompts to save/discard unsaved view changes before
 *            leaving). Called with the actual nav fn; must invoke it to proceed. Views
 *            that don't pass it navigate directly (default). #86bb5dx62 L-A4/L-C4.
 */
const MobileBottomNav = ({ activePage, fPlayback, mobileMenuOpen, onToggleMenu, onSetup, onLogout, guardNav, onPlayback: onPlaybackOverride }) => {
  // Playback = the multi-cam view (/mcplayback), labeled "Playback", shown per the fPlayback
  // USER RIGHT (same across all products). Legacy single-cam playback is retired.
  // David #86bb7phhh: the nav shows only where you can GO — the button for the CURRENT page is hidden
  // (no redundant "you are here" tab), so it reads as pure navigation and reclaims space.
  const guard = guardNav || ((fn) => fn && fn());
  const goSetup = onSetup || (() => history.push('/setup'));
  const onLive = activePage === 'live';
  const onPlayback = activePage === 'mcplayback' || activePage === 'playback';
  const onSetupPage = activePage === 'setup';
  return (
  <div className="live-mobile-nav">
    { !onLive &&
      <button
        className="live-mobile-nav__btn live-mobile-nav__btn--live"
        onClick={() => guard(() => history.push('/live'))}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
        </svg>
        <span className="live-mobile-nav__label">Live</span>
      </button>
    }
    { fPlayback === 1 && !onPlayback &&
      <button
        className="live-mobile-nav__btn live-mobile-nav__btn--playback"
        onClick={onPlaybackOverride || (() => guard(() => history.push('/mcplayback')))}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z"/>
        </svg>
        <span className="live-mobile-nav__label">Playback</span>
      </button>
    }
    { !onSetupPage &&
      <button
        className="live-mobile-nav__btn live-mobile-nav__btn--setup"
        onClick={() => guard(goSetup)}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z"/>
        </svg>
        <span className="live-mobile-nav__label">Setup</span>
      </button>
    }
    { onLogout &&
      <button className="live-mobile-nav__btn live-mobile-nav__btn--logout" onClick={() => guard(onLogout)}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
        </svg>
        <span className="live-mobile-nav__label">Logout</span>
      </button>
    }
  </div>
  );
};

export default MobileBottomNav;
